import React from "react";
import { Grid, Theme } from "@mui/material";
import { createStyles, makeStyles } from "@material-ui/styles";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import imgDefault from "../../../images/blog_home.webp";
import { useTranslation } from "react-i18next";
import { IArticleBlog } from "../../../utils/ArticleInterface";
import { createMarkup } from "../../../utils/App";
import { serverConfig } from "../../../utils/config";
import { useHistory } from "react-router";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    blog__card: {
      background: "transparent !important",
      border: "none !important",
      boxShadow: "none !important",
      display: 'block',
      flexDirection: (props: { layout: 'column' | 'row' }) => props.layout,
      width: '100%',
      padding: "24px 12px 12px 24px",
      margin: 'auto',
    },
    noPaddingLeft: {
      paddingLeft: "0 !important",
    },
    blog__grid: {
      [theme.breakpoints.down(1200)]: {
        display: "flex",
        justifyContent: "center",
      },
    },
    blog__cardContent: {
      minHeight: 100,
    },
    blog__btn: {
      fontSize: 14,
      fontFamily: "Josefin Sans",
      color: "#FFFFFF",
      textTransform: "capitalize",
      textAlign: "center",
      padding: "8px 14px",
      borderRadius: 100,
      background: "#890608",
      "&:hover": {
        background: "#B5551B",
      },
      [theme.breakpoints.up("sm")]: {
        minWidth: 180,
      },
    },
    clickable: {
      cursor: "pointer",
    },
    topDiv: {
      margin: 0, // Removes any default margin
      paddingBottom: 5, // Consistent spacing between `tagName` and `article.title`
      width: '100%', // Ensure full width
      [theme.breakpoints.down('sm')]: {
        fontSize: 16,
        margin: '10px 0px 5px 0px', // Adds bottom margin for spacing
      },
      [theme.breakpoints.up('md')]: {
        fontSize: 20,
        margin: '22px 0px 5px 0px', // Adds bottom margin for spacing
      },
    },
    secondDiv: {
      fontFamily: 'Roboto',
      margin: 0,
      fontWeight: 'bold',
      width: '100%', // Ensure full width
      [theme.breakpoints.down('sm')]: {
        fontSize: 16,
      },
      [theme.breakpoints.up('md')]: {
        fontSize: 24,
      },
    },
    thirdDiv: {
      fontFamily: 'Roboto',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      width: '100%', // Ensure full width
      [theme.breakpoints.down('sm')]: {
        fontSize: 14,
        marginBottom: 12,
      },
      [theme.breakpoints.up('md')]: {
        fontSize: 14,
        marginBottom: 18,
      },
    },
    cardMedia: {
      [theme.breakpoints.down('sm')]: {
        height: '100px',
        width: '150px',
        objectFit: 'fill',
      },
      [theme.breakpoints.up('md')]: {
        height: '188px',
        width: '313px',
        objectFit: 'fill',
      },
    },
    
    cardContent: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      padding: 0,  // Removes extra padding for consistent height control
      minHeight: 50, // Adjusts to 200px height for all sizes
      maxHeight: 200, // Ensures the height is fixed to 200px
      [theme.breakpoints.down('sm')]: {
        marginLeft: '10px',
      },
      [theme.breakpoints.up('md')]: {
        marginLeft: '15px',
      },
    },
      highlight: {
      backgroundColor: '#ffeb3b', // Yellow background for highlights
      padding: '0 2px',
      borderRadius: '2px',
      fontWeight: 'bold',
    },
  })
);

const Env = serverConfig;

interface ArticleProps {
  article: IArticleBlog;
  showIntroduction?: boolean; // Prop to control the visibility of the introduction
  layout?: 'column' | 'row'; // Prop to control the layout
  highlightTerm?: string;
  articleType?: string;
}
const Article = (props: ArticleProps) => {
  const { showIntroduction = true, layout = 'column', highlightTerm } = props; // Default values for the new props
  const classes = useStyles({ layout });
  const history = useHistory();
  const {i18n } = useTranslation<'common'>();
  const themeName = props.article.theme && props.article.theme.name
   ? i18n.language === 'fr'
     ? props.article.theme.name.fr
     : props.article.theme.name.en
   : ''; // Fallback if not populated


   // IMPROVED: Function to highlight search terms in text with better safety
    const highlightText = (text: string, term: string) => {
      if (!term || !text) return text;
      
      try {
        // Escape special regex characters in the search term
        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escapedTerm})`, 'gi');
        
        // Use a safer approach that doesn't rely on innerHTML
        return text.split(regex).map((part, index) => {
          if (index % 2 === 1) {
            // This part matches the search term
            return `<span class="${classes.highlight}">${part}</span>`;
          }
          return part;
        }).join('');
      } catch (error) {
        console.error('Error highlighting text:', error);
        return text;
      }
    };

    // IMPROVED: Function to safely strip HTML and truncate text
    const getIntroductionSnippet = (introduction: string, maxLength: number = 150) => {
      if (!introduction) return '';
      
      // Strip HTML tags if any
      const plainText = introduction.replace(/<[^>]*>/g, '');
      
      if (plainText.length <= maxLength) return plainText;
      
      // Truncate at the last space before maxLength to avoid cutting words
      const truncated = plainText.substring(0, maxLength);
      const lastSpaceIndex = truncated.lastIndexOf(' ');
      
      return lastSpaceIndex > maxLength * 0.8 
        ? truncated.substring(0, lastSpaceIndex) + '...'
        : truncated + '...';
    };

    // IMPROVED: Safe text processing
    const processTextForDisplay = (text: string, isTitle: boolean = false) => {
      if (!text) return '';
      
      let processedText = text;
      
      // For titles, we don't truncate, just highlight
      if (!isTitle) {
        processedText = getIntroductionSnippet(text);
      }
      
      // Apply highlighting if search term exists
      if (highlightTerm) {
        processedText = highlightText(processedText, highlightTerm);
      }
      
      return processedText;
    };

    const processedTitle = processTextForDisplay(props.article.title, true);
    const processedIntroduction = processTextForDisplay(props.article.introduction || '');

    /* const getArticleImageUrl = (photo: string | { filename: string } | undefined | null) => {
      if (!photo) return imgDefault;
      if (typeof photo === 'object' && 'filename' in photo) {
        return `${Env.ApiRoot}/images/articles/${photo.filename}`;
      }
      if (typeof photo === 'string') {
        return `${Env.ApiRoot}/images/articles/${photo}`;
      }
      return imgDefault;
    }; */

    const getArticleImageUrl = (photo: string | { 
        filename: string; 
        thumbnail?: string; 
        thumbnailUrl?: string;
        url?: string;
    } | undefined | null) => {
        if (!photo) return imgDefault;
        
        if (typeof photo === 'object') {
            // Priority 1: Use thumbnailUrl if available (full URL path)
            if (photo.thumbnailUrl) {
                // Check if it's already a full URL or needs the API root
                if (photo.thumbnailUrl.startsWith('http')) {
                    return photo.thumbnailUrl;
                } else {
                    return `${Env.ApiRoot}${photo.thumbnailUrl}`;
                }
            }
            
            // Priority 2: Use thumbnail filename if available
            if (photo.thumbnail) {
                return `${Env.ApiRoot}/images/articles/${photo.thumbnail}`;
            }
            
            // Priority 3: Use main image URL if available
            if (photo.url) {
                if (photo.url.startsWith('http')) {
                    return photo.url;
                } else {
                    return `${Env.ApiRoot}${photo.url}`;
                }
            }
            
            // Priority 4: Fall back to main image filename
            if ('filename' in photo) {
                return `${Env.ApiRoot}/images/articles/${photo.filename}`;
            }
        }
        
        if (typeof photo === 'string') {
            // Check if it's already a full URL
            if (photo.startsWith('http')) {
                return photo;
            }
            return `${Env.ApiRoot}/images/articles/${photo}`;
        }
        
        return imgDefault;
    };

    const getArticleRoute = () => {
      const articleType = props.article.articleType;
      
      // If articleType is 'Guide', use /guide/ route
      if (articleType === 'guide') {
        return `/guide/${props.article.slug}`;
      }
      
      // Default to /blog/ route for 'Standard', undefined, or any other value
      return `/blog/${props.article.slug}`;
    };

  //console.log("Fetched Theme Name:", themeName);
  //console.log("Fetched Category Name:", categoryName);


   return (
    <Grid item xs={12} md={12} sm={12} lg={12} className={classes.blog__grid}>
      <Card className={classes.blog__card}>
        <CardMedia
          component="img"
          height="280"
          image={getArticleImageUrl(props.article.photo)}
          alt={props.article.photoAltText}
          sx={{ borderRadius: '12px' }}
        />
        <CardContent className={`${classes.noPaddingLeft} ${classes.cardContent}`}>
          <div onClick={() => history.push(getArticleRoute())}>  
            <Typography
              gutterBottom
              color={"#890608"}
              fontFamily={"Roboto"}
              className={`${classes.topDiv} ${classes.clickable}`}
            >
              {themeName}
            </Typography>
          </div>
          <div 
            className={`${classes.secondDiv} ${classes.clickable}`}
            onClick={() => history.push(getArticleRoute())}
          >  
            <Typography
              gutterBottom
              color="text.secondary"
              className={classes.secondDiv}
              sx={{ fontWeight: 'bold' }}
              dangerouslySetInnerHTML={createMarkup(processedTitle)}
            />
          </div>
          {showIntroduction && (
            <div
              className={classes.clickable}
              onClick={() => history.push(getArticleRoute())}
            >
              <Typography
                gutterBottom
                color={"#7d7d7d"}
                className={classes.thirdDiv}
              >
                <div
                  className={classes.blog__cardContent}
                  dangerouslySetInnerHTML={createMarkup(processedIntroduction)}
                />
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default Article;
